import { Grid, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useCallback, useEffect, useState } from "react";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { useDateRange } from "contexts/DateRangeContext";
import dayjs, { Dayjs } from "dayjs";
import { MonthStepper } from "../common/MonthStepper";
import { TableHeader } from "../TableHeader";
import { GroupedDeliverySeries } from "../types";
import { DeliveriesOverviewPlot } from "./DeliveriesOverviewPlot";
import { DeliveriesOverviewTable } from "./DeliveriesOverviewTable";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { useIsAdmin, useTenant } from "hooks/settings";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { toLocalCalendarTime } from "src/utils/toLocalCalendarTime";

interface DeliveriesOverviewProps {
  groupedDeliverySeries: GroupedDeliverySeries;
}

export const DeliveriesOverview = ({
  groupedDeliverySeries,
}: DeliveriesOverviewProps) => {
  const { t } = useTenantTranslation();
  const isAdmin = useIsAdmin();
  const { startDate, endDate, setStartDate, setEndDate } = useDateRange();
  const tenantName = useTenant();
  const { localTimezone } = useLocalDatetime();

  const [selectedMaterials, setSelectedMaterials] =
    useLiteralListQueryParamState<string>({
      paramName: "materials",
      permitted: groupedDeliverySeries.availableMaterialNames,
      localStorageKey: `${tenantName}.strategyDashboard.deliveries.selectedMaterials`,
      defaultValue: groupedDeliverySeries.availableMaterialNames,
    });

  useEffect(() => {
    const now = toLocalCalendarTime(dayjs(), localTimezone);
    setStartDate(now.startOf("month"));
    setEndDate(now);
  }, [setStartDate, setEndDate, localTimezone]);

  const selectedMonth = dayjs.utc(startDate.format("YYYY-MM-01"));

  const handleMonthChange = useCallback(
    (month: Dayjs) => {
      setStartDate(month.startOf("month"));
      setEndDate(month.endOf("month"));
    },
    [setStartDate, setEndDate]
  );

  const [selectedDate, setSelectedDate] = useState(() =>
    getDefaultSelectedDate(groupedDeliverySeries)
  );

  const start = startDate.format();
  const end = endDate.format();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardDeliveriesAndPurchases.useLazyQuery();
  const getExcelBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      "Cache-Control": "no-cache",
    });

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <MonthStepper
          selectedMonth={selectedMonth}
          setSelectedMonth={handleMonthChange}
        />
        {isAdmin ? (
          <ExcelDownloadButton variant="outlined" getBlob={getExcelBlob} />
        ) : null}
      </Stack>
      <Stack direction="row" spacing={2}>
        <MultiSelect<string, string>
          options={groupedDeliverySeries.availableMaterialNames}
          getOptionId={(material) => material}
          getOptionLabel={(material) => t(material)}
          values={selectedMaterials}
          setValues={setSelectedMaterials}
          small
          selectAll
          labelAbove
          label={t("materials")}
        />
      </Stack>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 8 }} ml={0}>
          <DeliveriesOverviewPlot
            deliverySeries={groupedDeliverySeries}
            selectedMaterials={selectedMaterials}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TableHeader
            titlePrefix={t("deliveriesOn")}
            subtitleText={t("deliveriesTableSubtitleText")}
            selectedDate={selectedDate}
            formatDateMonthly={false}
          />
          <DeliveriesOverviewTable
            deliverySeries={groupedDeliverySeries}
            selectedDate={selectedDate}
          />
        </Grid>
      </Grid>
    </>
  );
};

const getDefaultSelectedDate = (
  deliverySeries: GroupedDeliverySeries
): Date | null => {
  const latestDate = deliverySeries.values
    .map(([, series]) => series.date[series.date.length - 1] ?? null)
    .reduce((max, current) => {
      if (max === null) return current;
      if (current === null) return max;
      return current > max ? current : max;
    }, null);
  return latestDate ? new Date(Date.parse(latestDate)) : null;
};
